import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { videoApi, apiConfig } from '../utils/apiConfig';
import VideoCard from './VideoCard';

const VideoList = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isMockData, setIsMockData] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        setIsMockData(apiConfig.isUsingMockData());
        
        const response = await videoApi.getAll({ page, limit: 12 });
        
        // Handle different response formats (mock vs real API)
        const data = response.data;
        if (data.data) {
          // Mock API format
          setVideos(data.data);
          setTotalPages(data.pagination?.totalPages || 1);
        } else {
          // Real API format
          setVideos(data.videos || []);
          setTotalPages(data.pagination?.totalPages || 1);
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch videos');
        setLoading(false);
        console.error(err);
      }
    };

    fetchVideos();
  }, [page]);

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchKeyword.trim()) {
      navigate(`/search?keyword=${encodeURIComponent(searchKeyword.trim())}`);
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
  );
  
  if (error) return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong className="font-bold">Error!</strong>
      <span className="block sm:inline"> {error}</span>
    </div>
  );

  return (
    <div className="video-list">
      {/* Search Box */}
      <div className="mb-8 bg-white p-6 rounded-lg shadow-sm">
        <h1 className="text-3xl font-bold mb-4 text-center">Project Bacchus</h1>
        <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="作品名、品番、女優名で検索..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
            />
            <button
              type="submit"
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-medium"
            >
              検索
            </button>
          </div>
        </form>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">作品一覧</h2>
        {isMockData && (
          <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
            🧪 仮データ表示中
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {videos.map((video) => (
          <VideoCard key={video.id} video={video} />
        ))}
      </div>
      
      {videos.length === 0 && (
        <div className="text-center py-8">
          <p>No videos found</p>
        </div>
      )}
      
      <div className="pagination flex justify-center mt-8">
        <button 
          onClick={handlePrevPage} 
          disabled={page === 1}
          className="px-4 py-2 mr-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span className="px-4 py-2">
          Page {page} of {totalPages}
        </span>
        <button 
          onClick={handleNextPage} 
          disabled={page === totalPages}
          className="px-4 py-2 ml-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default VideoList;
